import React from "react";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { onAuthStateChanged, setPersistence, browserSessionPersistence } from "firebase/auth";
import { doc, getDoc } from "firebase/firestore";
import { auth, db } from "./firebase";

import Login from "./pages/Login";

import EditOrder from "./pages/EditOrder";
import EditApprovedOrder from "./pages/EditApprovedOrder";
import ApproveOrder from "./pages/ApproveOrder";
import SalesControlCenter from "./pages/SalesControlCenter";
import SkuMaster from "./pages/SkuMaster";
import PartyMaster from "./pages/PartyMaster";
import BulkPartyImport from "./pages/BulkPartyImport";
import PartyEdit from "./pages/PartyEdit";
import PartyManager from "./pages/PartyManager";

import MasterPendingOrders from "./pages/MasterPendingOrders";
import BulkOrderUpload from "./pages/BulkOrderUpload";
import SystemSettings from "./pages/SystemSettings";
import OutletMaster from "./pages/OutletMaster";
import OutletOrderReport from "./pages/OutletOrderReport";
import OutletInvoices from "./pages/OutletInvoices";
import DsOutletMaster from "./pages/DsOutletMaster";
import PendingOrderSettings from "./pages/PendingOrderSettings";



import BillingReports from "./pages/BillingReports";
import OrderHeadReport from "./pages/OrderHeadReport";
import OrderLinesReport from "./pages/OrderLinesReport";


import Dashboard from "./pages/Dashboard";

import RequireAuth from "./components/RequireAuth";

import SalesSummary from "./pages/reports/SalesSummary";
import TallySheetReport from "./pages/reports/TallySheetReport";
import NewReport from "./pages/reports/NewReport";
import DmsUploadReport from "./pages/reports/DmsUploadReport";
import HsnSummaryReport from "./pages/reports/HsnSummaryReport";

import SalesmanLogin from "./pages/SalesmanLogin";
import SalesmanOrder from "./pages/SalesmanOrder";
import BasePage from "./pages/BasePage";
import FieldBilling from "./pages/FieldBilling";


import WholesaleOrder from "./pages/WholesaleOrder";
import Swd from "./pages/swd";

import { Navigate } from "react-router-dom";
import { useEffect, useState } from "react";


import WarehouseInward from "./pages/WarehouseInward";
import WarehouseClose from "./pages/WarehouseClose";
import SupervisorOverride from "./pages/SupervisorOverride";


import Cashier from "./pages/Cashier";
import AuditSku from "./pages/AuditSku";
import CashierApproval from "./pages/CashierApproval";
import SalesmanLedgerManager from "./pages/SalesmanLedgerManager";
import HawkerCDMManager from "./pages/HawkerCDMManager";
import CancelInvoices from "./pages/CancelInvoices";
import DeveloperAudit from "./pages/DeveloperAudit";
import CloudFunctionManager from "./pages/CloudFunctionManager";
import DashboardManager from "./pages/DashboardManager";
import EInvoiceGenerator from "./pages/EInvoiceGenerator";
import EWayBillGenerator from "./pages/EWayBillGenerator";
import CdmMaster from "./pages/CdmMaster";
import CdmBilling from "./pages/CdmBilling";
import CdmOrdersReport from "./pages/CdmOrdersReport";


function App() {
  const [role, setRole] = useState(null);
  const [authChecked, setAuthChecked] = useState(false);

  useEffect(() => {
    let unsub = null;

    const initApp = async () => {
      // 1. Fetch Supabase configuration from Firestore
      try {
        const snap = await getDoc(doc(db, "settings", "supabase_config"));
        if (snap.exists()) {
          const data = snap.data();
          if (data.url && data.anonKey) {
            const { updateSupabaseConfig } = await import("./supabase");
            updateSupabaseConfig(data.url, data.anonKey);
          }
        }
      } catch (err) {
        console.error("Failed to load dynamic Supabase config:", err);
      }

      // 2. Setup Auth Listener
      unsub = onAuthStateChanged(auth, async (user) => {
        if (!user) {
          setRole(null);
          setAuthChecked(true);
          return;
        }

        const snap = await getDoc(doc(db, "users", user.uid));
        if (snap.exists()) {
          const userRole = snap.data().role;
          setRole(userRole);

          // Security: Force non-salesmen to re-login after closing the tab
          if (userRole && userRole.toLowerCase().trim() !== "salesman") {
            setPersistence(auth, browserSessionPersistence).catch((err) =>
              console.error("Failed to update auth persistence:", err)
            );
          }
        } else {
          setRole(null);
        }

        setAuthChecked(true);
      });
    };

    initApp();

    return () => {
      if (unsub) unsub();
    };
  }, []);

  if (!authChecked) return null;

  const normalizedRole = role?.toLowerCase()?.trim();
  const withRoleGuard = (element, allowedRoles) => (
    <RequireAuth>
      {allowedRoles.includes(normalizedRole) ? element : <Navigate to="/" replace />}
    </RequireAuth>
  );

	
  return (
    <BrowserRouter>
      <Routes>
        <Route path="/" element={<Login />} />

        <Route path="/operator" element={<RequireAuth><SalesControlCenter /></RequireAuth>} />
        <Route path="/pending-orders" element={<RequireAuth><SalesControlCenter /></RequireAuth>} />
        <Route path="/edit-order/:orderId" element={<RequireAuth><EditOrder /></RequireAuth>} />
        <Route path="/approve-order/:orderId" element={<RequireAuth><ApproveOrder role={role}/></RequireAuth>} />
        <Route path="/edit-approved-order/:orderId" element={withRoleGuard(<EditApprovedOrder />, ["admin", "superadmin"])} />

        <Route path="/sku-master" element={withRoleGuard(<SkuMaster role={role} />, ["admin", "superadmin", "operator"])} />
        <Route path="/sku-manager" element={withRoleGuard(<SkuMaster role={role} />, ["admin", "superadmin"])} />
		<Route path="/party-manager" element={<RequireAuth><PartyManager /></RequireAuth>} />
        <Route path="/outlet-master" element={withRoleGuard(<OutletMaster />, ["admin", "superadmin", "operator"])} />

        <Route path="/reports" element={<RequireAuth><BillingReports /></RequireAuth>} />
        <Route path="/reports/order-head" element={<RequireAuth><OrderHeadReport /></RequireAuth>} />
        <Route path="/reports/order-lines" element={<RequireAuth><OrderLinesReport /></RequireAuth>} />
		
        <Route path="/reports/sales-summary" element={<RequireAuth><SalesSummary /></RequireAuth>} />
        <Route path="/reports/tally-sheet" element={<RequireAuth><TallySheetReport /></RequireAuth>} />
        <Route path="/reports/new-report" element={<RequireAuth><NewReport /></RequireAuth>} />
        <Route path="/reports/dms-upload" element={<RequireAuth><DmsUploadReport /></RequireAuth>} />
        <Route path="/reports/hsn-summary" element={<RequireAuth><HsnSummaryReport /></RequireAuth>} />

        {/* Inward + Closing for Admin, Superadmin, Operator */}
        {/* Unified Inward Register */}
        <Route path="/warehouse-inward" element={withRoleGuard(<WarehouseInward />, ["admin", "superadmin", "operator"])} />
        <Route path="/warehouse-close" element={withRoleGuard(<WarehouseClose />, ["admin", "superadmin", "operator", "warehouse"])} />

        <Route path="/supervisor-override" element={withRoleGuard(<SupervisorOverride />, ["admin", "superadmin", "operator"])} />
		
		
		<Route path="/master-pending" element={<RequireAuth><MasterPendingOrders /></RequireAuth>} />
		<Route path="/bulk-order-upload" element={withRoleGuard(<BulkOrderUpload />, ["admin", "superadmin", "operator"])} />




        <Route path="/salesman-login" element={<SalesmanLogin />} />
        <Route path="/salesman-order" element={withRoleGuard(<SalesmanOrder />, ["salesman", "wholesale", "swd", "npsr", "admin", "superadmin", "operator"])} />
        <Route path="/field-billing" element={withRoleGuard(<FieldBilling />, ["salesman", "wholesale", "swd", "npsr", "admin", "superadmin"])} />
		<Route path="/base" element={withRoleGuard(<BasePage />, ["salesman", "wholesale", "swd", "npsr", "admin", "superadmin", "operator"])} />

		<Route path="/wholesale-order" element={withRoleGuard(<WholesaleOrder />, ["wholesale"])} />
		<Route path="/swd" element={withRoleGuard(<Swd />, ["swd"])} />
		
		<Route path="/cashier" element={withRoleGuard(<Cashier />, ["collection", "admin", "superadmin", "salesman", "wholesale", "swd", "npsr", "cashier"])} />
        <Route path="/cashier-Approval" element={withRoleGuard(<CashierApproval />, ["cashier", "admin", "superadmin"])} />
        <Route path="/cdm-master" element={withRoleGuard(<CdmMaster />, ["admin", "superadmin", "operator"])} />
        <Route path="/cdm-billing" element={withRoleGuard(<CdmBilling />, ["salesman", "wholesale", "swd", "npsr", "admin", "superadmin", "operator"])} />
        <Route path="/reports/cdm-orders" element={withRoleGuard(<CdmOrdersReport />, ["admin", "superadmin", "operator"])} />
        <Route path="/audit-sku" element={withRoleGuard(<AuditSku />, ["admin", "superadmin", "operator"])} />
        <Route path="/salesman-ledger-manager" element={withRoleGuard(<SalesmanLedgerManager />, ["admin", "superadmin", "operator"])} />
        <Route path="/hawker-cdm" element={withRoleGuard(<HawkerCDMManager />, ["admin", "superadmin", "operator"])} />
        <Route path="/cancel-invoices" element={withRoleGuard(<CancelInvoices />, ["admin", "superadmin"])} />
        <Route path="/developer-audit" element={withRoleGuard(<DeveloperAudit />, ["admin", "superadmin"])} />
        <Route path="/cloud-functions" element={withRoleGuard(<CloudFunctionManager />, ["admin", "superadmin"])} />
        <Route path="/dashboard-manager" element={withRoleGuard(<DashboardManager />, ["admin", "superadmin", "operator"])} />
        <Route path="/settings" element={withRoleGuard(<SystemSettings />, ["admin", "superadmin", "operator"])} />
        <Route path="/settings/pending-report" element={withRoleGuard(<PendingOrderSettings />, ["admin", "superadmin", "operator"])} />
        <Route path="/reports/outlet-orders" element={<RequireAuth><OutletOrderReport /></RequireAuth>} />
        <Route path="/reports/outlet-invoices" element={<RequireAuth><OutletInvoices /></RequireAuth>} />
        <Route path="/ds-outlet-master" element={withRoleGuard(<DsOutletMaster />, ["admin", "superadmin"])} />
        <Route path="/einvoice" element={withRoleGuard(<EInvoiceGenerator />, ["admin", "superadmin", "operator"])} />
        <Route path="/ewaybill" element={withRoleGuard(<EWayBillGenerator />, ["admin", "superadmin", "operator"])} />
		
		
		
      </Routes>
    </BrowserRouter>
  );
}

export default App;
